/**
 * Flowlytics Pixel SDK - GTM Integration (Non-Invasive, Filtered)
 * Proxies dataLayer.push() to capture events without replacing GTM.
 * Filters noise: gtag init/config/set, GTM internal events, web vitals,
 * duplicate page_view, and resolves gtag positional arg patterns.
 */

class GTMIntegration {
  constructor(config, flowlytics) {
    this.config = config;
    this.flowlytics = flowlytics;
    this.originalPush = null;
    this.consentIntegration = null;

    // Standard ecommerce events (mapped 1:1 as event_type)
    this.ecommerceEvents = new Set([
      'purchase', 'add_to_cart', 'remove_from_cart', 'view_item',
      'view_item_list', 'begin_checkout', 'add_payment_info', 'add_shipping_info'
    ]);

    // GTM internal events to always filter
    this.internalEvents = new Set([
      'gtm.js', 'gtm.dom', 'gtm.load', 'gtm.init', 'gtm.init_consent',
      'gtm.click', 'gtm.linkClick', 'gtm.formSubmit', 'gtm.historyChange',
      'gtm.scrollDepth', 'gtm.timer', 'gtm.video',
      'consent_default', 'consent_init', 'optimize.domChange',
      'optimize.callback'
    ]);

    // Web vitals event names
    this.webVitals = new Set(['CLS', 'FID', 'LCP', 'INP', 'TTFB', 'FCP']);
  }

  /**
   * Connect to ConsentIntegration for delegating consent events
   */
  setConsentIntegration(consent) {
    this.consentIntegration = consent;
  }

  /**
   * Initialize GTM integration
   */
  init() {
    if (!window.dataLayer) {
      if (this.config.debug) {
        console.log('[Flowlytics GTM] dataLayer not found, skipping GTM integration');
      }
      return;
    }

    this.originalPush = window.dataLayer.push;
    const self = this;

    window.dataLayer.push = function (...args) {
      self.processGTMEvent(args);
      return self.originalPush.apply(this, args);
    };

    if (this.config.debug) {
      console.log('[Flowlytics GTM] Integration initialized (non-invasive, filtered)');
    }
  }

  /**
   * Process a dataLayer.push() call
   */
  processGTMEvent(args) {
    if (!args || args.length === 0) return;

    const eventData = args[0];
    if (typeof eventData !== 'object') return;

    // Handle gtag() array patterns: dataLayer.push(['command', arg1, arg2])
    if (Array.isArray(eventData)) {
      this.processGtagArray(eventData);
      return;
    }

    // Handle standard GTM object events: dataLayer.push({event: 'name', ...})
    const eventName = eventData.event;
    if (!eventName) return;

    // Filter GTM internal events (gtm.*, consent_*)
    if (this.isInternalEvent(eventName)) {
      if (this.config.debug) {
        console.log(`[Flowlytics GTM] Filtered internal event: ${eventName}`);
      }
      return;
    }

    // Filter duplicate page_view (SDK already tracks natively via trackPageView)
    if (eventName === 'page_view') {
      if (this.config.debug) {
        console.log('[Flowlytics GTM] Filtered duplicate page_view (SDK tracks natively)');
      }
      return;
    }

    // Filter web vitals (CLS, FID, LCP, etc.)
    if (this.isWebVital(eventName, eventData)) {
      if (this.config.debug) {
        console.log(`[Flowlytics GTM] Filtered web vital: ${eventName}`);
      }
      return;
    }

    // Map ecommerce events (event_type = the event name directly)
    if (this.ecommerceEvents.has(eventName)) {
      const ecommerceData = this.extractEcommerceData(eventData);
      this.flowlytics.track(eventName, {
        gtm_event: eventName,
        ...ecommerceData,
        event_source: 'gtm_datalayer'
      });
    } else {
      // Custom events (event_type = 'custom_event', real name in properties.event)
      // Strip the 'event' key from spread to avoid confusion
      const { event, ...rest } = eventData;
      this.flowlytics.track('custom_event', {
        event: eventName,
        gtm_event: eventName,
        ...rest,
        event_source: 'gtm_datalayer'
      });
    }

    if (this.config.debug) {
      console.log('[Flowlytics GTM] Event captured:', eventName);
    }
  }

  /**
   * Process gtag() array pushes: dataLayer.push(['command', ...args])
   * Patterns:
   *   ['js', Date]           → gtag init → SKIP
   *   ['config', 'GA-xxx']   → gtag config → SKIP
   *   ['set', {...}]         → gtag set → SKIP
   *   ['consent', 'update']  → consent → delegate to ConsentIntegration
   *   ['event', 'name', {}]  → RESOLVE event name and track
   */
  processGtagArray(data) {
    const command = data[0];

    // Delegate consent commands to ConsentIntegration
    if (command === 'consent') {
      if (this.consentIntegration && data[1] === 'update' && data[2]) {
        this.consentIntegration.handleConsentUpdate(data[2]);
      }
      return;
    }

    // Filter noise commands: gtag('js', date), gtag('config', ...), gtag('set', ...)
    if (command === 'js' || command === 'config' || command === 'set') {
      if (this.config.debug) {
        console.log(`[Flowlytics GTM] Filtered gtag('${command}', ...)`);
      }
      return;
    }

    // Resolve gtag('event', 'event_name', {properties})
    if (command === 'event' && data[1]) {
      const eventName = String(data[1]);
      const eventProps = (typeof data[2] === 'object' && data[2] !== null) ? data[2] : {};

      // Filter duplicate page_view
      if (eventName === 'page_view') {
        if (this.config.debug) {
          console.log('[Flowlytics GTM] Filtered gtag page_view (SDK tracks natively)');
        }
        return;
      }

      // Filter web vitals
      if (this.isWebVital(eventName, eventProps)) {
        if (this.config.debug) {
          console.log(`[Flowlytics GTM] Filtered gtag web vital: ${eventName}`);
        }
        return;
      }

      // Map ecommerce events directly, others as custom_event
      if (this.ecommerceEvents.has(eventName)) {
        this.flowlytics.track(eventName, {
          gtm_event: eventName,
          ...eventProps,
          event_source: 'gtm_datalayer'
        });
      } else {
        this.flowlytics.track('custom_event', {
          event: eventName,
          gtm_event: eventName,
          ...eventProps,
          event_source: 'gtm_datalayer'
        });
      }

      if (this.config.debug) {
        console.log(`[Flowlytics GTM] Resolved gtag('event', '${eventName}')`);
      }
      return;
    }

    // Unknown array patterns → skip
    if (this.config.debug) {
      console.log('[Flowlytics GTM] Filtered unknown array pattern:', command);
    }
  }

  /**
   * Check if event is a GTM internal event
   */
  isInternalEvent(name) {
    return this.internalEvents.has(name) || name.startsWith('gtm.');
  }

  /**
   * Check if event is a web vital metric
   */
  isWebVital(eventName, eventData) {
    if (this.webVitals.has(eventName)) return true;
    if (eventData && typeof eventData === 'object') {
      const cat = eventData.event_category || eventData.eventCategory;
      if (cat === 'Web Vitals' || cat === 'web-vitals') return true;
    }
    return false;
  }

  /**
   * Extract ecommerce data from dataLayer event
   */
  extractEcommerceData(eventData) {
    if (!eventData.ecommerce) return {};

    const ecommerce = eventData.ecommerce;
    return {
      transaction_id: ecommerce.transaction_id || ecommerce.purchase?.transaction_id,
      value: ecommerce.value || ecommerce.purchase?.value,
      currency: ecommerce.currency || ecommerce.purchase?.currency,
      items: ecommerce.items || ecommerce.purchase?.items || [],
      coupon: ecommerce.coupon,
      shipping: ecommerce.shipping,
      tax: ecommerce.tax
    };
  }

  /**
   * Restore original dataLayer.push
   */
  destroy() {
    if (this.originalPush && window.dataLayer) {
      window.dataLayer.push = this.originalPush;
      if (this.config.debug) {
        console.log('[Flowlytics GTM] Integration destroyed');
      }
    }
  }
}

export default GTMIntegration;
